import { DescriptionField } from "@dash/description-list";
import { PlacePrediction, useAutocomplete } from "@dash/google-map";
import { useTheme } from "@emotion/react";
import { useClickOutside } from "@mantine/hooks";
import React, { useEffect, useState } from "react";

import { fetchPlace } from "./fetchPlace";

export type RegionOwnerAddress = {
  country: string;
  city: string;
  street: string;
  postalCode: string;
  latitude: number | null;
  longitude: number | null;
};

type Props = {
  address?: RegionOwnerAddress;
  onAddressChange: (value: RegionOwnerAddress) => void;
  value?: string;
};

export const LocationPicker: React.FC<Props> = ({ address, onAddressChange, value }) => {
  const [inputValue, setInputValue] = useState(value ?? (address ? getSearchValueFromAddress(address) : ""));
  const [localAddress, setLocalAddress] = useState<RegionOwnerAddress>(
    address ?? {
      country: "",
      city: "",
      street: "",
      postalCode: "",
      latitude: null,
      longitude: null,
    },
  );

  useEffect(() => {
    onAddressChange(localAddress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localAddress]);

  const handleAddressChange = (value: RegionOwnerAddress) => {
    onAddressChange(value);
    setLocalAddress(value);
  };

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <AddressSearchField
        value={inputValue}
        onValueChange={setInputValue}
        onChange={handleAddressChange}
        predictionTypes={["geocode", "establishment"]}
      />
      <div css={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
        <DescriptionField label="Kraj">{localAddress.country || "-"}</DescriptionField>
        <DescriptionField label="Miasto">{localAddress.city || "-"}</DescriptionField>
        <DescriptionField label="Ulica">{localAddress.street || "-"}</DescriptionField>
        <DescriptionField label="Kod pocztowy">{localAddress.postalCode || "-"}</DescriptionField>
      </div>
    </div>
  );
};

const getSearchValueFromAddress = (address: RegionOwnerAddress) => {
  return [address.street, address.postalCode, address.city, address.country].filter(Boolean).join(", ");
};

type AddressSearchProps = {
  onChange: (value: RegionOwnerAddress) => void;
  value: string;
  onValueChange: (value: string) => void;
  label?: string;
};

export const AddressSearchField: React.FC<AddressSearchProps & { predictionTypes?: string[] }> = ({
  onChange,
  predictionTypes,
  value,
  onValueChange,
  label,
}) => {
  const theme = useTheme();

  const [isFocused, setIsFocused] = React.useState(false);

  const ref = useClickOutside(() => setIsFocused(false));

  const { predictions, searchPredictions } = useAutocomplete();

  const handlePredictionSelect = async (prediction: PlacePrediction) => {
    onValueChange(prediction.description);

    const place = await fetchPlace(prediction.id);

    if (!place) {
      return;
    }

    onChange(place);
    setIsFocused(false);
  };

  return (
    <div
      css={{ display: "flex", flexDirection: "column", position: "relative" }}
      onFocus={() => setIsFocused(true)}
      ref={ref}
    >
      <span
        css={{
          fontFamily: theme.displayFontFamily,
          fontSize: "14px",
          color: theme.colors.primary,
          marginBottom: "4px",
        }}
      >
        {label || "Wyszukiwanie adresu"}
      </span>

      <div css={{ position: "relative", display: "flex", alignItems: "center" }}>
        <input
          id="autocomplete"
          type="text"
          value={value}
          onChange={(e) => {
            onValueChange(e.target.value);
            searchPredictions(e.target.value, { includedTypes: predictionTypes });
          }}
          placeholder="Szukaj adresu..."
          css={{
            width: "100%",
            padding: "0.5rem 0.75rem",
            fontSize: "1rem",
            borderRadius: theme.smallBorderRadius,
            backgroundColor: theme.colors.primaryLight,
            border: "1px solid transparent",
            ":focus": {
              outline: "none",
              border: `1px solid ${theme.colors.primary}`,
            },
          }}
        />
      </div>

      {predictions.length > 0 && isFocused && (
        <ul
          css={{
            listStyleType: "none",
            padding: 0,
            backgroundColor: theme.colors.primaryLight,
            borderRadius: theme.smallBorderRadius,
            border: `1px solid ${theme.colors.border}`,
            marginTop: "4px",
            width: "100%",
            position: "absolute",
            top: "62px",
            zIndex: 1,
            overflow: "hidden",
          }}
        >
          {predictions.map((prediction) => (
            <li key={prediction.id}>
              <button
                onClick={() => handlePredictionSelect(prediction)}
                css={{
                  padding: "8px",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: theme.colors.primary,
                    color: "#fff",
                  },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  border: "none",
                  background: "none",
                  width: "100%",
                  textAlign: "left",
                }}
              >
                {prediction.description}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
